import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Download, Printer, Share2, Mail, CheckCircle2, X, 
  AlertTriangle, Clock, MapPin, User, Calendar, FileText, 
  Camera, MessageSquare, Minus, TrendingUp, ClipboardCheck,
  Loader2, ExternalLink, Save
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChecklistReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const reportRef = useRef(null);
  const printFrameRef = useRef(null);

  useEffect(() => {
    fetchChecklist();
  }, [id]);

  const fetchChecklist = async () => {
    try {
      const res = await fetch(`${API_URL}/api/checklists/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setChecklist(data);
      } else {
        toast.error('Checklist non trouvée');
        navigate('/forms');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '00:00:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateStats = () => {
    if (!checklist) return { total: 0, conforme: 0, nonConforme: 0, na: 0, score: 0 };
    
    let total = 0, conforme = 0, nonConforme = 0, na = 0;
    
    const items = checklist.sections 
      ? checklist.sections.flatMap(s => s.items || [])
      : checklist.items || [];
    
    items.forEach(item => {
      total++;
      if (item.response === 'conforme' || item.checked) conforme++;
      else if (item.response === 'non_conforme') nonConforme++;
      else if (item.response === 'non_applicable') na++;
    });
    
    const applicable = total - na;
    const score = applicable > 0 ? Math.round((conforme / applicable) * 100) : 100;
    
    return { total, conforme, nonConforme, na, score, applicable };
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return { label: 'Excellent', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (score >= 70) return { label: 'Acceptable', color: 'bg-amber-100 text-amber-700 border-amber-200' };
    return { label: 'Insuffisant', color: 'bg-red-100 text-red-700 border-red-200' };
  };

  const getNonConformItems = () => {
    if (!checklist) return [];
    
    const items = checklist.sections 
      ? checklist.sections.flatMap((s, sIdx) => 
          (s.items || []).map((item, iIdx) => ({ ...item, sectionTitle: s.title, sectionIndex: sIdx, itemIndex: iIdx }))
        )
      : (checklist.items || []).map((item, idx) => ({ ...item, itemIndex: idx }));
    
    return items.filter(item => item.response === 'non_conforme');
  };

  const getAllPhotos = () => {
    if (!checklist) return [];
    
    const photos = [];
    const items = checklist.sections 
      ? checklist.sections.flatMap((s) => 
          (s.items || []).map((item) => ({ ...item, sectionTitle: s.title }))
        )
      : checklist.items || [];
    
    items.forEach(item => {
      if (item.photos && item.photos.length > 0) {
        item.photos.forEach(photo => {
          photos.push({
            url: photo,
            itemText: item.text,
            sectionTitle: item.sectionTitle,
            response: item.response
          });
        });
      }
    });
    
    return photos;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Rapport - ${checklist?.title}`,
        text: `Rapport de contrôle: ${checklist?.title}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié dans le presse-papiers');
    }
  };

  // Generate PDF HTML content
  const generatePdfContent = () => {
    const stats = calculateStats();
    const scoreInfo = getScoreLabel(stats.score);
    const nonConformItems = getNonConformItems();
    
    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>Rapport - ${checklist?.title || 'Checklist'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1e293b; line-height: 1.5; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
    .title { font-size: 28px; font-weight: bold; color: #0f172a; }
    .subtitle { color: #64748b; margin-top: 5px; }
    .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
    .badge-excellent { background: #d1fae5; color: #047857; }
    .badge-acceptable { background: #fef3c7; color: #b45309; }
    .badge-insuffisant { background: #fee2e2; color: #dc2626; }
    .meta-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
    .meta-item { }
    .meta-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; }
    .meta-value { font-weight: 600; color: #1e293b; }
    .stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
    .stat-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; }
    .score-display { text-align: center; padding: 20px 0; }
    .score-number { font-size: 64px; font-weight: bold; }
    .score-green { color: #059669; }
    .score-amber { color: #d97706; }
    .score-red { color: #dc2626; }
    .score-label { color: #64748b; }
    .progress-bar { height: 12px; background: #e2e8f0; border-radius: 6px; overflow: hidden; margin-top: 15px; }
    .progress-fill { height: 100%; background: linear-gradient(to right, #059669, #10b981); }
    .summary-item { display: flex; justify-content: space-between; align-items: center; padding: 15px; border-radius: 8px; margin-bottom: 10px; }
    .summary-conforme { background: #d1fae5; }
    .summary-nonconforme { background: #fee2e2; }
    .summary-na { background: #f1f5f9; }
    .summary-icon { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 12px; }
    .summary-icon-conforme { background: #a7f3d0; color: #047857; }
    .summary-icon-nonconforme { background: #fecaca; color: #dc2626; }
    .summary-icon-na { background: #e2e8f0; color: #64748b; }
    .summary-count { font-size: 24px; font-weight: bold; }
    .section { margin-bottom: 30px; }
    .section-title { font-size: 18px; font-weight: bold; margin-bottom: 15px; display: flex; align-items: center; gap: 10px; }
    .section-title-icon { color: #3b82f6; }
    .reserves-section { border: 1px solid #fecaca; border-radius: 8px; overflow: hidden; }
    .reserves-header { background: #fee2e2; padding: 15px 20px; font-weight: bold; color: #dc2626; }
    .reserve-item { padding: 15px 20px; border-bottom: 1px solid #fee2e2; }
    .reserve-item:last-child { border-bottom: none; }
    .reserve-number { width: 28px; height: 28px; background: #fee2e2; color: #dc2626; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; margin-right: 12px; }
    .reserve-title { font-weight: 600; }
    .reserve-section-name { color: #64748b; font-size: 13px; }
    .reserve-comment { background: #fef2f2; padding: 10px 15px; border-radius: 6px; margin-top: 10px; color: #7f1d1d; font-size: 14px; }
    .detail-section { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .detail-header { background: #f8fafc; padding: 15px 20px; border-bottom: 1px solid #e2e8f0; }
    .detail-header-title { font-weight: 600; }
    .detail-header-desc { color: #64748b; font-size: 13px; }
    .detail-item { display: flex; align-items: center; padding: 12px 20px; border-bottom: 1px solid #f1f5f9; }
    .detail-item:last-child { border-bottom: none; }
    .detail-number { width: 28px; height: 28px; background: #f1f5f9; color: #64748b; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 13px; margin-right: 12px; flex-shrink: 0; }
    .detail-text { flex: 1; }
    .detail-badge { padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
    .detail-badge-conforme { background: #d1fae5; color: #047857; }
    .detail-badge-nonconforme { background: #fee2e2; color: #dc2626; }
    .detail-badge-na { background: #f1f5f9; color: #64748b; }
    .notes-section { background: #f8fafc; padding: 20px; border-radius: 8px; margin-top: 30px; }
    .notes-content { color: #475569; white-space: pre-wrap; }
    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 12px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <span class="badge badge-${stats.score >= 90 ? 'excellent' : stats.score >= 70 ? 'acceptable' : 'insuffisant'}">${scoreInfo.label}</span>
      <h1 class="title">${checklist?.title || ''}</h1>
      ${checklist?.description ? `<p class="subtitle">${checklist.description}</p>` : ''}
    </div>
    <div style="text-align: right;">
      <div style="color: #94a3b8; font-size: 12px;">Rapport généré le</div>
      <div style="font-weight: 600;">${format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}</div>
    </div>
  </div>

  <div class="meta-grid">
    ${checklist?.location ? `<div class="meta-item"><div class="meta-label">Lieu</div><div class="meta-value">${checklist.location}</div></div>` : ''}
    <div class="meta-item"><div class="meta-label">Contrôleur</div><div class="meta-value">${checklist?.created_by_name || 'Non spécifié'}</div></div>
    <div class="meta-item"><div class="meta-label">Date du contrôle</div><div class="meta-value">${format(new Date(checklist?.updated_at || checklist?.created_at), 'dd MMM yyyy', { locale: fr })}</div></div>
    <div class="meta-item"><div class="meta-label">Durée</div><div class="meta-value">${formatTime(checklist?.elapsed_time)}</div></div>
  </div>

  <div class="stats-grid">
    <div class="stat-card">
      <h3 style="font-weight: 600; margin-bottom: 10px;">Score de conformité</h3>
      <div class="score-display">
        <div class="score-number ${stats.score >= 90 ? 'score-green' : stats.score >= 70 ? 'score-amber' : 'score-red'}">${stats.score}%</div>
        <div class="score-label">${stats.conforme} conforme${stats.conforme > 1 ? 's' : ''} sur ${stats.applicable} applicable${stats.applicable > 1 ? 's' : ''}</div>
        <div class="progress-bar"><div class="progress-fill" style="width: ${stats.score}%"></div></div>
      </div>
    </div>
    <div class="stat-card">
      <h3 style="font-weight: 600; margin-bottom: 15px;">Résumé des points</h3>
      <div class="summary-item summary-conforme">
        <div style="display: flex; align-items: center;">
          <div class="summary-icon summary-icon-conforme">✓</div>
          <span style="font-weight: 500; color: #047857;">Conformes</span>
        </div>
        <span class="summary-count" style="color: #047857;">${stats.conforme}</span>
      </div>
      <div class="summary-item summary-nonconforme">
        <div style="display: flex; align-items: center;">
          <div class="summary-icon summary-icon-nonconforme">✗</div>
          <span style="font-weight: 500; color: #dc2626;">Non conformes</span>
        </div>
        <span class="summary-count" style="color: #dc2626;">${stats.nonConforme}</span>
      </div>
      <div class="summary-item summary-na">
        <div style="display: flex; align-items: center;">
          <div class="summary-icon summary-icon-na">—</div>
          <span style="font-weight: 500; color: #64748b;">Non applicables</span>
        </div>
        <span class="summary-count" style="color: #64748b;">${stats.na}</span>
      </div>
    </div>
  </div>

  ${nonConformItems.length > 0 ? `
  <div class="section">
    <div class="reserves-section">
      <div class="reserves-header">⚠ Réserves et non-conformités (${nonConformItems.length})</div>
      ${nonConformItems.map((item, idx) => `
        <div class="reserve-item">
          <div style="display: flex; align-items: flex-start;">
            <span class="reserve-number">${idx + 1}</span>
            <div style="flex: 1;">
              <div class="reserve-title">${item.text}</div>
              ${item.sectionTitle ? `<div class="reserve-section-name">Section: ${item.sectionTitle}</div>` : ''}
              ${item.comment ? `<div class="reserve-comment">💬 ${item.comment}</div>` : ''}
              ${item.photos && item.photos.length > 0 ? `
                <div style="margin-top: 10px; display: flex; gap: 8px; flex-wrap: wrap;">
                  ${item.photos.map(photo => `<img src="${photo}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 6px; border: 1px solid #fecaca;" />`).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `).join('')}
    </div>
  </div>
  ` : ''}

  <div class="section">
    <h3 class="section-title">📋 Détail complet du contrôle</h3>
    <div class="detail-section">
      ${checklist?.sections ? checklist.sections.map(section => `
        <div class="detail-header">
          <div class="detail-header-title">${section.title}</div>
          ${section.description ? `<div class="detail-header-desc">${section.description}</div>` : ''}
        </div>
        ${(section.items || []).map((item, idx) => `
          <div class="detail-item">
            <span class="detail-number">${idx + 1}</span>
            <span class="detail-text">${item.text}</span>
            <span class="detail-badge ${item.response === 'conforme' || item.checked ? 'detail-badge-conforme' : item.response === 'non_conforme' ? 'detail-badge-nonconforme' : item.response === 'non_applicable' ? 'detail-badge-na' : 'detail-badge-na'}">
              ${item.response === 'conforme' || item.checked ? '✓ Conforme' : item.response === 'non_conforme' ? '✗ Non conforme' : item.response === 'non_applicable' ? '— N/A' : 'Non vérifié'}
            </span>
          </div>
        `).join('')}
      `).join('') : (checklist?.items || []).map((item, idx) => `
        <div class="detail-item">
          <span class="detail-number">${idx + 1}</span>
          <span class="detail-text">${item.text}</span>
          <span class="detail-badge ${item.response === 'conforme' || item.checked ? 'detail-badge-conforme' : item.response === 'non_conforme' ? 'detail-badge-nonconforme' : item.response === 'non_applicable' ? 'detail-badge-na' : 'detail-badge-na'}">
            ${item.response === 'conforme' || item.checked ? '✓ Conforme' : item.response === 'non_conforme' ? '✗ Non conforme' : item.response === 'non_applicable' ? '— N/A' : 'Non vérifié'}
          </span>
        </div>
      `).join('')}
    </div>
  </div>

  ${checklist?.notes ? `
  <div class="section">
    <h3 class="section-title">💬 Notes et observations</h3>
    <div class="notes-section">
      <div class="notes-content">${checklist.notes}</div>
    </div>
  </div>
  ` : ''}

  ${getAllPhotos().length > 0 ? `
  <div class="section">
    <h3 class="section-title">📷 Photos du contrôle (${getAllPhotos().length})</h3>
    <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 15px;">
      ${getAllPhotos().map((photo, idx) => `
        <div>
          <img src="${photo.url}" style="width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 8px; border: 1px solid #e2e8f0;" />
          <p style="font-size: 12px; margin-top: 5px; color: #475569;">${photo.itemText}</p>
        </div>
      `).join('')}
    </div>
  </div>
  ` : ''}

  <div class="footer">
    <p>Rapport généré automatiquement par le système de gestion des checklists</p>
    <p>Référence: ${checklist?.id || ''}</p>
  </div>
</body>
</html>
    `;
  };

  // Open PDF preview in new window
  const handleExportPdf = () => {
    setPdfPreviewOpen(true);
  };

  // Print the PDF
  const handlePrint = () => {
    const pdfContent = generatePdfContent();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Save as PDF (opens print dialog with save option)
  const handleSavePdf = () => {
    const pdfContent = generatePdfContent();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(pdfContent);
    printWindow.document.close();
    printWindow.focus();
    toast.info('Utilisez "Enregistrer en PDF" dans le dialogue d\'impression');
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="report-loading">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="flex flex-col items-center justify-center h-screen" data-testid="report-not-found">
        <FileText className="w-16 h-16 text-slate-300 mb-4" />
        <p className="text-slate-500">Rapport non trouvé</p>
        <Button onClick={() => navigate('/forms')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux checklists
        </Button>
      </div>
    );
  }

  const stats = calculateStats();
  const scoreInfo = getScoreLabel(stats.score);
  const nonConformItems = getNonConformItems();

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="checklist-report-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/forms')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              data-testid="back-to-forms-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour aux checklists
            </button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleShare} data-testid="share-report-btn">
                <Share2 className="w-4 h-4 mr-1" />
                Partager
              </Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleExportPdf} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-1" />
                Ouvrir en PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto px-4 py-8 print:py-4" ref={reportRef}>
        {/* Report Header */}
        <div className="bg-white rounded-xl shadow-sm border p-8 mb-6 print:shadow-none print:border-none">
          <div className="flex items-start justify-between mb-6">
            <div>
              <Badge className={`${scoreInfo.color} border mb-3`} data-testid="score-badge">
                {scoreInfo.label}
              </Badge>
              <h1 className="text-3xl font-bold text-slate-900" data-testid="report-title">
                {checklist.title}
              </h1>
              {checklist.description && (
                <p className="text-slate-500 mt-2">{checklist.description}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Rapport généré le</p>
              <p className="font-medium">{format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}</p>
            </div>
          </div>

          {/* Meta Information */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
            {checklist.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Lieu</p>
                  <p className="font-medium text-sm" data-testid="report-location">{checklist.location}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Contrôleur</p>
                <p className="font-medium text-sm" data-testid="report-inspector">{checklist.created_by_name || 'Non spécifié'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Date du contrôle</p>
                <p className="font-medium text-sm" data-testid="report-date">
                  {format(new Date(checklist.updated_at || checklist.created_at), 'dd MMM yyyy', { locale: fr })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Durée</p>
                <p className="font-medium text-sm" data-testid="report-duration">{formatTime(checklist.elapsed_time)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Score Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="print:shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Score de conformité
                </h3>
              </div>
              <div className="text-center py-4">
                <div className={`text-6xl font-bold ${getScoreColor(stats.score)}`} data-testid="conformity-score">
                  {stats.score}%
                </div>
                <p className="text-slate-500 mt-2">
                  {stats.conforme} conforme{stats.conforme > 1 ? 's' : ''} sur {stats.applicable} applicable{stats.applicable > 1 ? 's' : ''}
                </p>
                <Progress value={stats.score} className="h-3 mt-4" />
              </div>
            </CardContent>
          </Card>

          <Card className="print:shadow-none">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-4">
                <ClipboardCheck className="w-5 h-5 text-blue-500" />
                Résumé des points
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg" data-testid="conforme-count">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-medium text-emerald-700">Conformes</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600">{stats.conforme}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg" data-testid="non-conforme-count">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <X className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="font-medium text-red-700">Non conformes</span>
                  </div>
                  <span className="text-2xl font-bold text-red-600">{stats.nonConforme}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg" data-testid="na-count">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <Minus className="w-5 h-5 text-slate-600" />
                    </div>
                    <span className="font-medium text-slate-700">Non applicables</span>
                  </div>
                  <span className="text-2xl font-bold text-slate-600">{stats.na}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Non-Conformities Section */}
        {nonConformItems.length > 0 && (
          <Card className="mb-6 border-red-200 print:shadow-none">
            <CardHeader className="bg-red-50 border-b border-red-200">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Réserves et non-conformités ({nonConformItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-red-100">
                {nonConformItems.map((item, idx) => (
                  <div key={idx} className="p-4 hover:bg-red-50/50" data-testid={`non-conform-item-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-900">{item.text}</h4>
                            {item.sectionTitle && (
                              <p className="text-sm text-slate-500">Section: {item.sectionTitle}</p>
                            )}
                          </div>
                          <Badge className="bg-red-100 text-red-700 border-red-200">Non conforme</Badge>
                        </div>
                        {item.comment && (
                          <div className="mt-3 p-3 bg-red-50 rounded-lg">
                            <p className="text-sm text-slate-700 flex items-start gap-2">
                              <MessageSquare className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                              {item.comment}
                            </p>
                          </div>
                        )}
                        {item.photos && item.photos.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm text-slate-600 mb-2 flex items-center gap-1">
                              <Camera className="w-4 h-4" />
                              Photos jointes ({item.photos.length})
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              {item.photos.map((photo, photoIdx) => (
                                <img 
                                  key={photoIdx}
                                  src={photo} 
                                  alt={`Photo ${photoIdx + 1}`}
                                  className="w-24 h-24 object-cover rounded-lg border border-red-200"
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Items Detail */}
        <Card className="mb-6 print:shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Détail complet du contrôle
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {checklist.sections ? (
              checklist.sections.map((section, sIdx) => (
                <div key={sIdx} className="border-b last:border-b-0">
                  <div className="p-4 bg-slate-50 border-b">
                    <h4 className="font-semibold text-slate-700">{section.title}</h4>
                    {section.description && (
                      <p className="text-sm text-slate-500">{section.description}</p>
                    )}
                  </div>
                  <div className="divide-y">
                    {section.items?.map((item, iIdx) => (
                      <div key={iIdx} className="p-4 flex items-center gap-4" data-testid={`detail-item-${sIdx}-${iIdx}`}>
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-medium text-sm">
                          {iIdx + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-800">{item.text}</p>
                        </div>
                        <div>
                          {item.response === 'conforme' || item.checked ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Conforme
                            </Badge>
                          ) : item.response === 'non_conforme' ? (
                            <Badge className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                              <X className="w-3 h-3" />
                              Non conforme
                            </Badge>
                          ) : item.response === 'non_applicable' ? (
                            <Badge className="bg-slate-100 text-slate-600 border-slate-200 flex items-center gap-1">
                              <Minus className="w-3 h-3" />
                              N/A
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 border-slate-200">
                              Non vérifié
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="divide-y">
                {checklist.items?.map((item, idx) => (
                  <div key={idx} className="p-4 flex items-center gap-4" data-testid={`detail-item-${idx}`}>
                    <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-medium text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{item.text}</p>
                    </div>
                    <div>
                      {item.response === 'conforme' || item.checked ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Conforme
                        </Badge>
                      ) : item.response === 'non_conforme' ? (
                        <Badge className="bg-red-100 text-red-700 border-red-200 flex items-center gap-1">
                          <X className="w-3 h-3" />
                          Non conforme
                        </Badge>
                      ) : item.response === 'non_applicable' ? (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 flex items-center gap-1">
                          <Minus className="w-3 h-3" />
                          N/A
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200">
                          Non vérifié
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes Section */}
        {checklist.notes && (
          <Card className="mb-6 print:shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                Notes et observations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-700 whitespace-pre-wrap" data-testid="report-notes">{checklist.notes}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Photos Section */}
        {getAllPhotos().length > 0 && (
          <Card className="mb-6 print:shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-blue-500" />
                Photos du contrôle ({getAllPhotos().length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {getAllPhotos().map((photo, idx) => (
                  <div key={idx} className="group">
                    <div className="aspect-square rounded-lg overflow-hidden border bg-slate-100">
                      <img 
                        src={photo.url} 
                        alt={`Photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="mt-2">
                      <p className="text-sm font-medium text-slate-700 truncate">{photo.itemText}</p>
                      {photo.sectionTitle && (
                        <p className="text-xs text-slate-500">{photo.sectionTitle}</p>
                      )}
                      {photo.response === 'non_conforme' && (
                        <Badge className="mt-1 bg-red-100 text-red-700 text-xs">Non conforme</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500 py-6 border-t print:mt-8">
          <p>Rapport généré automatiquement par le système de gestion des checklists</p>
          <p className="mt-1">Référence: {checklist.id}</p>
        </div>
      </div>

      {/* PDF Preview Dialog */}
      <Dialog open={pdfPreviewOpen} onOpenChange={setPdfPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden [&>button]:hidden">
          <DialogHeader className="p-4 border-b bg-slate-50">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Aperçu du rapport PDF
              </DialogTitle>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-pdf-btn">
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimer
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSavePdf} data-testid="save-pdf-btn">
                  <Save className="w-4 h-4 mr-1" />
                  Enregistrer PDF
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                  onClick={() => setPdfPreviewOpen(false)}
                  data-testid="close-pdf-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[calc(90vh-80px)] bg-slate-200 p-4">
            <div 
              className="bg-white shadow-lg mx-auto max-w-3xl"
              style={{ minHeight: '800px' }}
              dangerouslySetInnerHTML={{ __html: generatePdfContent() }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-none { border: none !important; }
          .print\\:bg-white { background-color: white !important; }
          .print\\:py-4 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
        }
      `}</style>
    </div>
  );
}
